import { ChangeEvent } from 'react';
import { X, Camera } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface AadhaarVerificationProps {
  aadharNumber: string;
  onAadharNumberChange: (value: string) => void;
  onDocumentFrontUpload: (file: File) => void;
  documentFrontPreview?: string;
  documentFrontFile?: File;
  onRemoveDocumentFront?: () => void;
  errors?: {
    aadharNumber?: string;
    identityDocumentFront?: string;
  };
}

const AadhaarVerification = ({
  aadharNumber,
  onAadharNumberChange,
  onDocumentFrontUpload,
  documentFrontPreview,
  documentFrontFile,
  onRemoveDocumentFront,
  errors,
}: AadhaarVerificationProps) => {
  const handleAadharNumberChange = (value: string) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 12);
    onAadharNumberChange(cleanedValue);
  };

  const handleFrontDocumentChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onDocumentFrontUpload(file);
      e.target.value = '';
    }
  };

  const handleRemoveDocument = () => {
    onRemoveDocumentFront?.();
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="aadharNumber">Aadhaar Number</Label>
        <Input
          id="aadharNumber"
          placeholder="123456789012"
          value={aadharNumber}
          onChange={e => handleAadharNumberChange(e.target.value)}
          autoComplete="on"
          maxLength={12}
          className={errors?.aadharNumber ? 'border-red-500' : ''}
        />
        {errors?.aadharNumber && (
          <p className="text-sm text-destructive">{errors.aadharNumber}</p>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="identityDocumentFront">Aadhar Card - Front</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-4">
            <Input
              id="identityDocumentFront"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={handleFrontDocumentChange}
            />
            <label htmlFor="identityDocumentFront" className="cursor-pointer">
              <div className="flex flex-col items-center justify-center w-auto h-20 text-center">
                {documentFrontPreview ? (
                  <div className="relative inline-block">
                    <img
                      src={documentFrontPreview}
                      alt="Identity Document Front"
                      className="max-w-full max-h-20 object-contain rounded-lg"
                      onError={e => {
                        console.error('Image load error:', e);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={e => {
                        e.currentTarget.style.display = 'block';
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={e => {
                        e.preventDefault();
                        handleRemoveDocument();
                      }}
                      className="absolute top-0 right-0 p-1"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Camera className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {documentFrontFile
                        ? documentFrontFile.name
                        : 'Click to upload front side'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      PNG, JPG, JPEG up to 10MB
                    </span>
                  </>
                )}
              </div>
            </label>
          </div>
          {errors?.identityDocumentFront && !documentFrontFile && (
            <p className="text-sm text-destructive">
              {errors.identityDocumentFront}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AadhaarVerification;
